/**
 * Bubble View Module
 * Orchestrator: wires together state, physics, render, and interaction modules
 * Pure view: receives state, emits events via callbacks
 * Physics-based nested circle visualization with zoom/pan
 */

import * as Store from "../../store/index.js";
import { visibleViewRect } from '../../ui/view-centering.js';
import * as State from "./state.js";
import * as Physics from "./physics.js";
import * as Render from "./render.js";
import * as Interaction from "./interaction.js";
import * as CanvasRenderer from "./canvas-renderer.js";
import * as LabelLOD from "./label-lod.js";
import * as Titles from "./titles.js";
import { isMobile, isMobileShellVisible } from "../../../../shared/utils/device-detect.js";
import { t } from "../../../../shared/i18n/index.js";
import { bubbleInitialHintHtml } from "../shortcut-hints.js";
import { bubblePositionsLoaded, loadBubblePositions } from "../../store/bubble-positions.js";
import { fitAllIcon } from "../../mobile/icons.js";

// Canvas renderer: default ON for mobile (a 715-node map costs ~2s of DOM
// construction alone on a phone; the canvas paints the same scene in tens of
// milliseconds). Opt out with `localStorage.orbital_canvas_bubble = '0'`.
// Desktop keeps the DOM renderer — drag-reparent, hover previews, badges and
// curved labels are DOM-only affordances.
function canvasModeEnabled() {
  try {
    return isMobile() && localStorage.getItem("orbital_canvas_bubble") !== "0";
  } catch {
    return false;
  }
}

// ============================================================================
// MODULE OPTIONS FACTORY
// ============================================================================

/**
 * Create state accessor object for passing to sub-modules
 * Uses callback injection pattern to avoid circular dependencies
 */
function createStateAccessor() {
  return {
    getContainer: State.getContainer,
    getCallbacks: State.getCallbacks,
    getCurrentState: State.getCurrentState,
    getCurrentDocId: State.getCurrentDocId,
    getWorld: State.getWorld,
    getCanvas: State.getCanvas,
    getDragLayer: State.getDragLayer,
    getBState: State.getBState,
    setBState: State.setBState,
    setBStateField: State.setBStateField,
    getHoveredBubbleId: State.getHoveredBubbleId,
    setHoveredBubbleId: State.setHoveredBubbleId,
    getRetryCount: State.getRetryCount,
    setRetryCount: State.setRetryCount,
    getFocused: State.getFocused,
    setFocused: State.setFocused,
    getLastTreeStructureHash: State.getLastTreeStructureHash,
    setLastTreeStructureHash: State.setLastTreeStructureHash,
    updateTransform: State.updateTransform,
    clampZoom: State.clampZoom,
    fitToView: () => fitToView(),
    computeTreeStructureHash: State.computeTreeStructureHash,
    findNodeById: State.findNodeById,
    MIN_SIZE: State.MIN_SIZE,
    PADDING: State.PADDING,
  };
}

/**
 * Create physics accessor object for passing to sub-modules
 */
function createPhysicsAccessor() {
  return {
    getPhysicsNodes: Physics.getPhysicsNodes,
    setPhysicsNodes: Physics.setPhysicsNodes,
    isRunning: Physics.isRunning,
    isRenderPending: Physics.isRenderPending,
    setRenderPending: Physics.setRenderPending,
    startPhysics: Physics.startPhysics,
    cancelPhysics: Physics.cancelPhysics,
    updateBubblePos: Physics.updateBubblePos,
    saveAllPositions: Physics.saveAllPositions,
  };
}

// ============================================================================
// RENDER HELPERS
// ============================================================================

/**
 * Create render options for renderBubbles
 */
function createRenderOptions() {
  const stateAccessor = createStateAccessor();
  const physicsAccessor = createPhysicsAccessor();

  return {
    state: stateAccessor,
    physics: physicsAccessor,
    canvasMode: canvasModeEnabled(),
    onLayoutSettled: afterLayout,
    beforeLayout,
    setupBubbleDrag: (el, node, pNode, dragOptions) => {
      Interaction.setupBubbleDrag(el, node, pNode, {
        state: stateAccessor,
        physics: physicsAccessor,
        ...dragOptions,
      });
    },
    zoomTo: (id) => {
      const callbacks = State.getCallbacks();
      State.zoomTo(
        id,
        callbacks?.onNavigate,
        () => Render.renderBubbles(createRenderOptions())
      );
    },
  };
}

/**
 * Trigger a render with current options
 */
let loadingLayoutOf = null;

function doRender() {
  // The map's saved layout comes from the device first: drawn without it,
  // the whole map would be laid out again.
  const docId = State.getCurrentDocId();
  if (!bubblePositionsLoaded(docId)) {
    if (loadingLayoutOf === docId) return;
    loadingLayoutOf = docId;
    loadBubblePositions(docId).then(() => {
      if (loadingLayoutOf === docId) loadingLayoutOf = null;
      if (State.getContainer() && State.getCurrentDocId() === docId) scheduleRenderWithPhysicsCheck(true);
    });
    return;
  }
  Render.renderBubbles(createRenderOptions());
  afterLayout();
  // Canvas mode: repaint after every rebuild; keeps the loop alive while the
  // physics simulation (if any) runs. No-op when the canvas isn't mounted.
  CanvasRenderer.markDirty();
}

/**
 * Before a layout runs, a fit waiting for it frames the bubbles' first places
 * (and is done again once they settle): the layout of a large map animates
 * the groups seen with the view (physics.js), and the view would otherwise
 * still be the zoom 1 a first opening starts from, inside one bubble.
 */
function beforeLayout() {
  if (!State.hasFitRequest()) return;
  const canvas = State.getCanvas();
  const bounds = getContentBounds();
  if (!canvas || !bounds) return;
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) return;
  State.setFittedView(State.fitTransform(bounds, rect.width, rect.height));
  State.updateTransform({ save: false });
}

/**
 * Once the bubbles are placed (after a render, or at the end of the layout
 * simulation): the zoom floor follows the map, and a requested fit happens.
 */
function afterLayout() {
  if (Physics.isRunning()) return; // the simulation's end calls again
  const canvas = State.getCanvas();
  const bounds = getContentBounds();
  if (!canvas || !bounds) return;
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) return;
  const fit = State.fitTransform(bounds, rect.width, rect.height); // also sets the floor
  if (State.takeFitRequest()) {
    State.setFittedView(fit);
    State.updateTransform();
  }
}

/**
 * Show the whole map (the focused bubble's content), centered, at most at
 * true size: the Fit button. While the layout simulates, at its end.
 */
export function fitToView() {
  State.requestFit();
  afterLayout();
}

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Render the Bubble view
 * @param {HTMLElement} containerEl - Container element
 * @param {Object} state - { data, focusId, selectedId, hideCompleted, docId }
 * @param {Object} cbs - Callbacks for actions
 */
export function render(containerEl, state, cbs) {
  // Initialize state
  State.setContainer(containerEl);
  State.setCallbacks(cbs);
  State.setCurrentState(state);
  State.setCurrentDocId(state.currentDocId || state.docId || "default");

  // Create the bubble canvas structure
  containerEl.innerHTML = `
    <div class="breadcrumb" id="b-crumbs"></div>
    <div class="bubble-canvas" id="b-canvas">
      <div class="bubble-world" id="b-world"></div>
      <div class="bubble-drag" id="b-drag"></div>
    </div>
    <div class="controls">
      <button class="ctrl-btn" id="b-zin">+</button>
      <div class="zoom-lvl" id="b-zlvl">100%</div>
      <button class="ctrl-btn" id="b-zout">\u2212</button>
      <button class="ctrl-btn" id="b-reset" style="margin-top: 6px; font-size: 13px">\u27F2</button>
      <button class="ctrl-btn" id="b-fit" title="${t("views.bubble.fit")}" aria-label="${t("views.bubble.fit")}">${fitAllIcon()}</button>
    </div>
    <div class="shortcut-help" id="b-hint">
      ${bubbleInitialHintHtml()}
    </div>
  `;

  // The titles, drawn on a canvas over the bubbles (laptop view; the phone's
  // canvas draws its own). A double click on a parent's title edits it.
  Titles.unmount();
  if (!canvasModeEnabled()) {
    Titles.mount(containerEl.querySelector("#b-canvas"), State.getBState, {
      onEdit: (id, x, y) => {
        const cb = State.getCallbacks();
        if (!cb?.isReadOnly?.()) cb?.onNodeEdit?.(id, x, y);
      },
    });
  }

  // Set DOM references
  State.setDomRefs({
    world: containerEl.querySelector("#b-world"),
    canvas: containerEl.querySelector("#b-canvas"),
    dragLayer: containerEl.querySelector("#b-drag"),
  });

  // Restore transform from Store; otherwise fit the map once it is laid
  // out (afterLayout). A saved zoom under 0.15 is an old leaked mobile
  // preview transform (see the mindmap restore note) or a large map seen
  // whole: fitting covers both. Zoom 1 at the origin, the default before
  // 2026-10-15, put a large map's viewer inside one bubble, with nothing
  // to see (20,000 items).
  //
  // Exception: while the MOBILE SHELL is visible and the live state carries
  // a preview-owned transform (_mobilePreview, stamped by
  // setDesktopViewTransform for pinch/pan/fit), keep it. Mobile previews
  // never write the Store transform, so restoring here snapped the user's
  // view back to the desktop-saved one on every full render (remote edits,
  // shell remounts).
  if (!(isMobileShellVisible() && State.getBState()?._mobilePreview)) {
    const savedTransform = Store.get("bubbleTransform");
    if (savedTransform && savedTransform.zoom >= 0.15) {
      State.setBState(savedTransform);
      State.setLastSavedTransform(savedTransform);
    } else {
      State.setBState({ zoom: 1, panX: 0, panY: 0 });
      State.setLastSavedTransform({ zoom: 1, panX: 0, panY: 0 });
      if (!isMobileShellVisible()) State.requestFit(); // the phone fits its own preview
    }
  }

  // What a layout run can show moving: the view as it is when the run starts.
  // The canvas size the render read before adding bubbles: reading it again
  // here made the browser style and lay out the new bubbles at once, then
  // again for the frame.
  Physics.setSeenTest(() => {
    let rect = LabelLOD.viewSize();
    if (!rect.width || !rect.height) rect = State.getCanvas()?.getBoundingClientRect();
    if (!rect?.width || !rect.height) return null;
    const view = State.getBState();
    return (n) => LabelLOD.movesVisibly(n, view, rect.width, rect.height);
  });

  // Setup interactions
  const stateAccessor = createStateAccessor();

  Interaction.setupZoomControls({
    state: stateAccessor,
  });

  Interaction.setupCanvasInteraction({
    state: stateAccessor,
  });

  // Canvas mode: paint bubbles to a single canvas instead of building DOM.
  // render.js skips element creation entirely (options.canvasMode) — the
  // hidden world/labels layers stay empty.
  if (canvasModeEnabled()) {
    const canvasViewport = containerEl.querySelector("#b-canvas");
    canvasViewport?.classList.add("bubble-canvas-spike");

    // Full render() rebuilds the container HTML, orphaning any previously
    // mounted canvas — remount cleanly into the fresh viewport. (Happens on
    // every in-place refresh, e.g. the mobile fold buttons' explicit
    // renderDesktopView call.)
    if (CanvasRenderer.isMounted()) CanvasRenderer.unmount();

    CanvasRenderer.mount({
      host: canvasViewport,
      getPhysicsNodes: Physics.getPhysicsNodes,
      getViewTransform: State.getBState,
      getSelectedId: () => Store.get("selectedId"),
      isPhysicsRunning: Physics.isRunning,
      onTap: (nodeId) => {
        const cbs = State.getCallbacks();
        if (cbs?.onNodeSelect) cbs.onNodeSelect(nodeId);
      },
      onLongPress: (nodeId) => {
        const cbs = State.getCallbacks();
        cbs?.onNodeLongPress?.(nodeId);
      },
    });
  }

  State.updateTransform();
  doRender();
}

/**
 * Update the view with new state
 * Only re-renders when necessary to prevent flickering
 * @param {Object} prevState - Previous state
 * @param {Object} nextState - New state
 */
export function refreshRoutineNodes() {
  if(State.getContainer())scheduleRenderWithPhysicsCheck(true);
}

export function update(prevState, nextState) {
  // Note: We do NOT save positions here - positions are saved when:
  // 1. Physics simulation converges (startPhysics -> saveAllPositions)
  // 2. User manually drags a bubble (onUp -> setBubblePosition)

  State.setCurrentState(nextState);
  State.setCurrentDocId(
    nextState.currentDocId || nextState.docId || State.getCurrentDocId() || "default"
  );

  const container = State.getContainer();
  const callbacks = State.getCallbacks();

  if (!container || !callbacks) return;

  // Handle selection change without full re-render
  if (prevState?.selectedId !== nextState?.selectedId) {
    if (CanvasRenderer.isMounted()) {
      CanvasRenderer.markDirty(); // canvas reads selectedId live at paint time
    } else {
      State.updateSelectionVisual(nextState.selectedId);
    }
  }

  // Check for obvious non-structural changes that don't need re-render
  const focusChanged = prevState?.focusId !== nextState?.focusId;
  const hideCompletedChanged =
    prevState?.hideCompleted !== nextState?.hideCompleted;
  const docIdChanged =
    (prevState?.currentDocId || prevState?.docId) !==
    (nextState?.currentDocId || nextState?.docId);

  const tagFilterChanged =
    prevState?.hiddenTagIds !== nextState?.hiddenTagIds ||
    prevState?.highlightedTagIds !== nextState?.highlightedTagIds ||
    prevState?.horizon !== nextState?.horizon; // horizon cut-off is a filter too

  // These changes always require re-render and should interrupt physics
  if (focusChanged || hideCompletedChanged || docIdChanged || tagFilterChanged) {
    // A new focus shows its content whole (breadcrumb, Up, sidebar).
    if (focusChanged && !isMobileShellVisible()) State.requestFit();
    State.setLastTreeStructureHash(null); // Force structural change detection
    scheduleRenderWithPhysicsCheck(true);
    return;
  }

  // For data changes, use structural hash to detect if tree structure actually changed
  // This prevents physics interruption for property-only updates (name, status, dates, etc.)
  const dataRefChanged = prevState?.data !== nextState?.data;
  if (dataRefChanged && nextState?.data) {
    const focusNode = nextState.focusId
      ? State.findNodeById(nextState.data, nextState.focusId) || nextState.data
      : nextState.data;
    const newHash = State.computeTreeStructureHash(focusNode, nextState.hideCompleted);
    const lastHash = State.getLastTreeStructureHash();

    if (newHash !== lastHash) {
      // Actual structural change (nodes added/removed/moved)
      State.setLastTreeStructureHash(newHash);
      scheduleRenderWithPhysicsCheck(true); // Interrupt physics - structure changed
    } else if (CanvasRenderer.isMounted()) {
      // Property-only change, canvas mode: refresh the physics nodes' display
      // data from the new tree and repaint.
      Render.refreshDisplayData(nextState.data, createStateAccessor(), Physics.getPhysicsNodes());
      CanvasRenderer.markDirty();
    } else {
      // Property-only change - update bubble visuals immediately without full re-render
      // This allows status/name changes to show during physics
      const stateAccessor = createStateAccessor();
      Render.updateBubbleProperties(nextState.data, stateAccessor);
      // The titles are drawn from the physics nodes' display data.
      Render.refreshDisplayData(nextState.data, stateAccessor, Physics.getPhysicsNodes());
      Titles.schedule();
    }
  }
}

/**
 * Schedule a render with physics state checking
 * @param {boolean} forceImmediate - If true, interrupt physics
 */
function scheduleRenderWithPhysicsCheck(forceImmediate = false) {
  const physicsState = {
    isRunning: Physics.isRunning,
    cancelFn: Physics.cancelPhysics,
    setRenderPending: Physics.setRenderPending,
  };

  State.scheduleRender(forceImmediate, doRender, physicsState);
}

/** Navigation waits for the active physics/layout pass to finish. */
export function isLayoutPending() {
  return Physics.isRunning() || State.getRenderScheduled() || loadingLayoutOf !== null;
}

/**
 * Pan to a rendered bubble without changing zoom or physics positions. From
 * its place in the layout, not its element: a bubble off screen or too small
 * is not displayed (label-lod.js), and is the one most worth centering.
 */
export function centerOnNode(nodeId) {
  const node = Physics.getPhysicsNodes().find((n) => n.id === nodeId);
  const canvas = State.getCanvas();
  const visible = visibleViewRect(canvas);
  if (!node || !visible) return false;
  const center = LabelLOD.worldCenter(node);
  const rect = canvas.getBoundingClientRect();
  const transform = State.getBState();
  const x = rect.left + canvas.clientLeft + transform.panX + center.x * transform.zoom;
  const y = rect.top + canvas.clientTop + transform.panY + center.y * transform.zoom;
  State.setBState({...transform, panX: transform.panX + visible.x - x, panY: transform.panY + visible.y - y});
  State.updateTransform();
  return true;
}

/**
 * World-space bounding box of all bubbles, from the physics nodes. Used by
 * the mobile fit-to-view when the canvas renderer is active (there are no DOM
 * bubbles to measure). Returns null when nothing is rendered.
 */
export function getContentBounds() {
  const nodes = Physics.getPhysicsNodes() || [];
  if (!nodes.length) return null;
  const abs = new Map();
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    let ax = n.offsetX + n.x;
    let ay = n.offsetY + n.y;
    if (n.parent) {
      const pa = abs.get(n.parent.id);
      if (pa) {
        ax += pa.x - n.parent.radius;
        ay += pa.y - n.parent.radius;
      }
    }
    abs.set(n.id, { x: ax, y: ay });
    minX = Math.min(minX, ax - n.radius);
    minY = Math.min(minY, ay - n.radius);
    maxX = Math.max(maxX, ax + n.radius);
    maxY = Math.max(maxY, ay + n.radius);
  }
  return { minX, minY, maxX, maxY };
}

/**
 * Where to edit a parent's title (keyboard Enter / F2): the middle of its
 * drawn title, in client coordinates, or null when it is not drawn.
 * @param {string} nodeId
 * @returns {{x: number, y: number}|null}
 */
export function titleEditPoint(nodeId) {
  return Titles.titlePoint(nodeId);
}

/**
 * Clean up the view
 */
export function destroy() {
  if (CanvasRenderer.isMounted()) CanvasRenderer.unmount();
  Titles.unmount();
  Physics.cancelPhysics();
  Physics.resetPhysicsState();
  State.resetState();
  Render.clearCaches();
  Render.forgetBubbles();
  LabelLOD.clear();
}

/**
 * Convert screen coordinates to world coordinates
 * Re-exported from interaction module for external use
 * @param {number} screenX - Screen X coordinate
 * @param {number} screenY - Screen Y coordinate
 * @param {string} [parentId] - Optional parent node ID
 * @returns {{x: number, y: number}} World coordinates
 */
export function screenToWorld(screenX, screenY, parentId) {
  return Interaction.screenToWorld(screenX, screenY, parentId, {
    state: createStateAccessor(),
    physics: createPhysicsAccessor(),
  });
}

// ============================================================================
// VIEW METADATA
// ============================================================================

/**
 * View information for view switcher
 */
export const viewInfo = {
  name: "bubble",
  get label() { return t("views.name.bubble"); },
  icon: "circle",
};
